import React, { useState, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import apiClient from '../api/client';
import Grid from '@mui/material/Unstable_Grid2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

export default function StockFileStatus() {
    const [inventoryFileDate, setInventory] = useState(null);
    const [smartCartDate, setSmartCart] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const [smartCartRes, invRes] = await Promise.allSettled([
                    apiClient.get('/LastSmartCheckUpdate'),
                    apiClient.get('/LastInventoryUpdate')
                ]);

                if (smartCartRes.status === 'fulfilled' && smartCartRes.value?.data?.creation_date) {
                    setSmartCart(new Date(smartCartRes.value.data.creation_date).toLocaleString());
                } else {
                    setSmartCart('Not Available');
                }

                if (invRes.status === 'fulfilled' && invRes.value?.data?.creation_date) {
                    setInventory(new Date(invRes.value.data.creation_date).toLocaleString());
                } else {
                    setInventory('Not Available');
                }
            } catch (error) {
                console.error('Error fetching stock file status:', error);
                setSmartCart('Error');
                setInventory('Error');
            } finally {
                setLoading(false);
            }
        };

        fetchStatus();
    }, []);

    const unavailable = (v) => v === 'Not Available' || v === 'Error';
    const failedCount = [smartCartDate, inventoryFileDate].filter(unavailable).length;
    let statusChip = null;
    if (!loading) {
        if (failedCount === 0) {
            statusChip = { label: 'Synced', color: 'success', icon: <CheckCircleOutlinedIcon style={{ fontSize: 14 }} /> };
        } else if (failedCount === 1) {
            statusChip = { label: 'Partial', color: 'warning', icon: <WarningAmberOutlinedIcon style={{ fontSize: 14 }} /> };
        } else {
            statusChip = { label: 'Unavailable', color: 'error', icon: <WarningAmberOutlinedIcon style={{ fontSize: 14 }} /> };
        }
    }

    const files = [
        {
            title: 'SmartCart File',
            timestamp: smartCartDate,
            description: 'Automated distributor feed sync',
        },
        {
            title: 'Inventory File',
            timestamp: inventoryFileDate,
            description: 'Primary ERP stock snapshot',
        },
    ];

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeOutlinedIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Stock File Status
                    </Typography>
                </Box>
                {statusChip && (
                    <Chip
                        icon={statusChip.icon}
                        label={statusChip.label}
                        size="small"
                        color={statusChip.color}
                        sx={{ fontSize: '0.75rem' }}
                    />
                )}
            </Box>

            <Grid container spacing={2}>
                {files.map((file, idx) => (
                    <Grid key={idx} xs={12} sm={6}>
                        <Paper
                            sx={{
                                p: 2.5,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                '&:hover': { borderColor: 'secondary.dark' },
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: 'text.secondary',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        letterSpacing: '0.04em',
                                        mb: 1,
                                    }}
                                >
                                    {file.title}
                                </Typography>

                                {loading ? (
                                    <Skeleton variant="text" width="80%" height={36} />
                                ) : (
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            color: file.timestamp === 'Not Available' || file.timestamp === 'Error' ? 'text.disabled' : 'text.primary',
                                            mb: 0.5,
                                        }}
                                    >
                                        {file.timestamp ?? '—'}
                                    </Typography>
                                )}
                            </Box>

                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                {file.description}
                            </Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}